import { z } from "zod";

/**
 * Typed, validated access to the web app's build-time environment.
 *
 * `parseEnv` is pure (takes the source map explicitly) so it is unit-testable
 * without Vite's `import.meta.env`. `getEnv` is the lazy, memoized accessor
 * the running app uses — it never evaluates at module load, so importing this
 * file in tests does not throw on a missing real environment.
 */
const schema = z.object({
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().min(1),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),
  // Web Push (FCM) VAPID public key. Optional: when absent, push registration
  // is treated as unsupported on this device rather than failing the boot.
  VITE_FIREBASE_VAPID_KEY: z.string().optional(),
  // Exact Identity Platform tenant id of the superadmin console tenant (mirrors
  // the backend's MADEMIND_TENANT_SLUG). The app shows the superadmin console
  // ONLY for this exact tenant — never a slug-prefix match. Optional +
  // fail-closed: when unset, NO tenant is treated as the console (safer than a
  // false positive). On praesto-dev this is `mademind-praesto-dev-1lz2x`.
  VITE_MADEMIND_TENANT_SLUG: z.string().optional(),
  // reCAPTCHA Enterprise site key App Check attests this web app with (C-16).
  // PUBLIC by design (it ships in the page). Optional: when absent App Check
  // stays off and our /api calls carry no token (local dev, emulators).
  VITE_RECAPTCHA_ENTERPRISE_SITE_KEY: z.string().optional(),
  VITE_USE_EMULATORS: z.enum(["true", "false"]).optional(),
  VITE_EMULATOR_AUTH: z.string().optional(),
  VITE_EMULATOR_FIRESTORE: z.string().optional(),
  VITE_EMULATOR_STORAGE: z.string().optional(),
});

export interface AppEnv {
  readonly firebase: {
    readonly apiKey: string;
    readonly authDomain: string;
    readonly projectId: string;
    readonly storageBucket: string;
    readonly messagingSenderId: string;
    readonly appId: string;
    readonly vapidKey?: string;
  };
  /** Exact console tenant id; absent → the superadmin console is disabled. */
  readonly mademindTenantSlug?: string;
  /** reCAPTCHA Enterprise site key for App Check; absent → App Check is off. */
  readonly appCheckSiteKey?: string;
  readonly useEmulators: boolean;
  readonly emulators: {
    readonly auth: string;
    readonly firestore: string;
    readonly storage: string;
  };
}

export function parseEnv(source: Record<string, unknown>): AppEnv {
  const result = schema.safeParse(source);
  if (!result.success) {
    const detail = result.error.issues
      .map((i) => `${i.path.join(".")}: ${i.message}`)
      .join("; ");
    throw new Error(`Invalid web environment — ${detail}`);
  }
  const e = result.data;
  return {
    firebase: {
      apiKey: e.VITE_FIREBASE_API_KEY,
      authDomain: e.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: e.VITE_FIREBASE_PROJECT_ID,
      storageBucket: e.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: e.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: e.VITE_FIREBASE_APP_ID,
      ...(e.VITE_FIREBASE_VAPID_KEY ? { vapidKey: e.VITE_FIREBASE_VAPID_KEY } : {}),
    },
    ...(e.VITE_MADEMIND_TENANT_SLUG ? { mademindTenantSlug: e.VITE_MADEMIND_TENANT_SLUG } : {}),
    ...(e.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY
      ? { appCheckSiteKey: e.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY }
      : {}),
    useEmulators: e.VITE_USE_EMULATORS === "true",
    emulators: {
      auth: e.VITE_EMULATOR_AUTH ?? "http://127.0.0.1:9099",
      firestore: e.VITE_EMULATOR_FIRESTORE ?? "127.0.0.1:8080",
      storage: e.VITE_EMULATOR_STORAGE ?? "127.0.0.1:9199",
    },
  };
}

let cached: AppEnv | undefined;

/** Memoized accessor over Vite's `import.meta.env`. App-only. */
export function getEnv(): AppEnv {
  if (!cached) {
    const source = (import.meta as unknown as { env: Record<string, unknown> })
      .env;
    cached = parseEnv(source);
  }
  return cached;
}
